import React from 'react';
import {
  isCoverageEndorsementsState,
  isUnderwriterContext,
  type PrimeXUnderwriteQuoteHandlers,
  type UnderwriteQuoteState,
} from '../types';
import type { CoverageEndorsementsState } from '../../../../../policies/prime-x/endorsements/types';

/**
 * `state` is what the screen renders — under the draft flag that is the
 * projection, committed state with the staged log replayed on top.
 * `committedState` is what actually exists on the quote: no staged edit is in
 * it until it has been accepted.
 *
 * They are separate, named channels on purpose. Anything that PERSISTS (the
 * quote-action payloads in `useUnderwriteActionHandlers`) must read
 * `committedState`; anything that DISPLAYS reads `state`. Collapsing them into
 * one slot is how staged edits reached the backend before (final review, F1).
 */
type UnderwriteState =
  | {
      state: UnderwriteQuoteState;
      committedState: UnderwriteQuoteState;
      handlers: PrimeXUnderwriteQuoteHandlers;
    }
  | {
      state: CoverageEndorsementsState;
      committedState: CoverageEndorsementsState;
      handlers: PrimeXUnderwriteQuoteHandlers;
    };

const UnderwriteStateContext = React.createContext<UnderwriteState | null>(
  null
);

export const UnderwriteStateProvider: React.FC<
  Readonly<{
    children: React.ReactNode;
    state: UnderwriteQuoteState | CoverageEndorsementsState;
    /** Committed state. Flows that never stage pass the same value as `state`. */
    committedState: UnderwriteQuoteState | CoverageEndorsementsState;
    handlers: PrimeXUnderwriteQuoteHandlers;
  }>
> = ({ children, state, committedState, handlers }) => {
  const value = {
    state,
    committedState,
    handlers,
  } as UnderwriteState;

  return (
    <UnderwriteStateContext.Provider value={value}>
      {children}
    </UnderwriteStateContext.Provider>
  );
};

export const useUnderwriterContext = () => {
  const context = React.useContext(UnderwriteStateContext);
  if (!context) {
    throw new Error(
      'useSharedQuoteStateContext must be used within a SharedQuoteStateProvider'
    );
  }

  if (isUnderwriterContext(context)) {
    return { state: context.state, handlers: context.handlers } as {
      state: UnderwriteQuoteState;
      handlers: PrimeXUnderwriteQuoteHandlers;
    };
  }

  return context;
};

/**
 * The committed quote — never the draft projection. Read this, and only this,
 * to build anything that is sent to the backend.
 *
 * Returns `null` outside the provider and in the MTE/coverage-endorsements
 * flow, which has no underwriter quote to commit. That is the same shape
 * `useUnderwriteActionHandlers` already uses to opt out, so a caller cannot
 * accidentally fall back to draft state.
 */
export const useCommittedUnderwriteState = (): UnderwriteQuoteState | null => {
  const context = React.useContext(UnderwriteStateContext);

  if (!context) {
    return null;
  }

  const committed: UnderwriteQuoteState | CoverageEndorsementsState =
    context.committedState;

  if (isCoverageEndorsementsState(committed)) {
    return null;
  }

  return committed;
};

/**
 * Optional version of useUnderwriterContext. Returns null when used outside
 * UnderwriteStateProvider. Use in components that render in both Customize
 * (no provider) and Edit/Underwriter (with provider), e.g. when only needed
 * for UW-only UI like the referral strategy dropdown.
 */
export const useUnderwriterContextOptional = (): UnderwriteState | null => {
  return React.useContext(UnderwriteStateContext);
};
